// Вход по ссылке и запасному коду (030: contracts/api.md, «Вход по ссылке», «Учётная запись»;
// research Р1, Р4; ADR-0044; ASG-ONB-02, ASG-ONB-03, ASG-ONB-04, ASG-ONB-14, ASG-ONB-15).
// Токен стоит в адресе страницы /enter/{token} и маршрутов /api/login-links/{token}: в журнал
// запросов он не пишется, ответы — с Referrer-Policy: no-referrer (INVITE_PATHS). Открытие
// страницы (GET) ссылку не тратит, обмен на сеанс — только по «Войти» (FR-004).
import { z } from '../kernel/zod.ts';
import { IdSchema } from '../kernel/common.ts';
import { EMAIL_MAX, isEmailAddress } from '../kernel/email.ts';
import { fieldMessages } from '../kernel/field-messages.ts';

/** Ссылка действует столько дней (auth.loginLink.days; правило базы — не больше 7). */
export const LOGIN_LINK_MAX_DAYS = 7;
/** Сеанс по ссылке держится столько дней (auth.linkSession.days; правило базы — не больше 30). */
export const LINK_SESSION_MAX_DAYS = 30;
/** Запасной код — столько цифр (FR-005). */
export const LOGIN_CODE_LENGTH = 6;
/** Неверных кодов по одной ссылке, после которых её код больше не принимается (data-model.md). */
export const LOGIN_CODE_FAILURES_MAX = 5;
/** «Помощь»: текст вопроса и название экрана (FR-032). */
export const HELP_TEXT_MAX = 2000;
export const HELP_SCREEN_MAX = 100;
/** Куда ведёт вход по ссылке и по коду (FR-007). */
export const LINK_LOGIN_NEXT = '/my';
/** 032 (FR-021): вход по ссылке из копии руководителю — «Просрочено у ваших сотрудников». */
export const LINK_LOGIN_TEAM = '/team';

/**
 * Откуда ссылка: «Первое письмо» или запрос человека («Войти по ссылке на почту», «Прислать
 * на n***@…»); 032 — team: письмо руководителю, вход ведёт на LINK_LOGIN_TEAM.
 */
export const loginLinkPurposes = ['first_letter', 'request', 'team'] as const;
export type LoginLinkPurpose = (typeof loginLinkPurposes)[number];
export const LoginLinkPurposeSchema = z.enum(loginLinkPurposes);

/** Почему ссылка отозвана (data-model.md, `access.login_links.revoked_reason`). */
export const loginLinkRevokeReasons = [
  'replaced',
  'not_me',
  'person_changed',
  'mail_off',
  'controller',
] as const;
export type LoginLinkRevokeReason = (typeof loginLinkRevokeReasons)[number];
export const LoginLinkRevokeReasonSchema = z.enum(loginLinkRevokeReasons);

/** Вид сеанса (`access.sessions.scope`): обычный или по ссылке — только «свои пункты». */
export const sessionScopes = ['account', 'link'] as const;
export type SessionScope = (typeof sessionScopes)[number];
export const SessionScopeSchema = z.enum(sessionScopes);

/** Токен ссылки — 32 случайных байта в base64url (43 знака); формат проверяется мягко. */
export const LoginLinkTokenParamsSchema = z.object({
  token: z.string().regex(/^[A-Za-z0-9_-]{16,128}$/),
});
export type LoginLinkTokenParams = z.infer<typeof LoginLinkTokenParamsSchema>;

/**
 * GET /api/login-links/{token}. valid — «Вы входите как Новиков Н. Н. Это не вы?» и «Войти»;
 * current — в браузере уже открыт сеанс этого человека: сразу «Мои задачи» без обмена (FR-007).
 * stale — устарела, использована или отозвана: «Ссылка устарела» и «Прислать на n***@…».
 * gone — запись человека в архиве или склеена: «Ссылка больше не действует» (FR-011).
 */
export const LoginLinkInfoSchema = z.discriminatedUnion('state', [
  z.object({
    state: z.literal('valid'),
    name: z.string(),
    emailMasked: z.string(),
    current: z.boolean(),
  }),
  z.object({ state: z.literal('stale'), emailMasked: z.string().nullable() }),
  z.object({ state: z.literal('gone') }),
]);
export type LoginLinkInfo = z.infer<typeof LoginLinkInfoSchema>;

/** Пустое тело действий ссылки: обмен, «Это не я», «Прислать». */
export const EmptyBodySchema = z.strictObject({});
export type EmptyBody = z.input<typeof EmptyBodySchema>;

/** Ответ обмена ссылки и входа по коду: куда перейти; cookie сеанса `link` — в заголовке. */
export const LinkLoginResponseSchema = z.object({
  next: z.union([z.literal(LINK_LOGIN_NEXT), z.literal(LINK_LOGIN_TEAM)]),
});
export type LinkLoginResponse = z.infer<typeof LinkLoginResponseSchema>;

/**
 * 202 «Войти по ссылке на почту»: одинаково для любой почты (FR-010) — только срок ссылки, дней
 * (auth.loginLink.days), для строки «Ссылка действует N дней».
 */
export const LoginLinkRequestedSchema = z.object({ linkDays: z.number().int().positive() });
export type LoginLinkRequested = z.infer<typeof LoginLinkRequestedSchema>;

/** 202 «Прислать на n***@…»: ответ одинаков, ушло письмо или нет (FR-010). */
export const LoginLinkResendResponseSchema = z.object({ emailMasked: z.string() });
export type LoginLinkResendResponse = z.infer<typeof LoginLinkResendResponseSchema>;

const loginEmail = z
  .string({ error: fieldMessages.loginEmail })
  .trim()
  // Пустая почта — один текст «Введите почту», без второго о формате (как 025).
  .min(1, { error: fieldMessages.loginEmail, abort: true })
  .max(EMAIL_MAX, { error: fieldMessages.recoveryEmail })
  .refine(isEmailAddress, { error: fieldMessages.recoveryEmail });

/** POST /api/auth/login-link — «Войти по ссылке на почту»: ответ 202 одинаков для любой почты. */
export const RequestLoginLinkBodySchema = z.strictObject({ email: loginEmail });
export type RequestLoginLinkBody = z.input<typeof RequestLoginLinkBodySchema>;

/** Запасной код — ровно 6 цифр; пробелы по краям не мешают. */
export const LoginCodeSchema = z
  .string({ error: fieldMessages.loginCode })
  .trim()
  .regex(new RegExp(`^\\d{${LOGIN_CODE_LENGTH}}$`), { error: fieldMessages.loginCode });

/**
 * POST /api/auth/login-code — «Войти по коду из письма»: почта ссылки и код. Неверный код —
 * AUTH-011; пять неверных подряд по почте — блокировка AUTH-002 (FR-005).
 */
export const LoginByCodeBodySchema = z.strictObject({
  email: z
    .string({ error: fieldMessages.loginEmail })
    .trim()
    .min(1, { error: fieldMessages.loginEmail })
    .max(EMAIL_MAX, { error: fieldMessages.loginEmail }),
  code: LoginCodeSchema,
});
export type LoginByCodeBody = z.input<typeof LoginByCodeBodySchema>;

/**
 * POST /api/help — «Помощь» (FR-032): текст, название экрана и пункт, если вопрос о нём.
 * Контролёрам — уведомление с текстом; в событие текст не попадает.
 */
export const HelpBodySchema = z.strictObject({
  text: z
    .string({ error: fieldMessages.helpText })
    .trim()
    .min(1, { error: fieldMessages.helpText })
    .max(HELP_TEXT_MAX, { error: fieldMessages.helpText }),
  screen: z.string().trim().max(HELP_SCREEN_MAX),
  itemId: IdSchema.optional(),
});
export type HelpBody = z.input<typeof HelpBodySchema>;

/** Человек справочника, с которым связана учётная запись (FR-001): для «Вы вошли как …». */
export const MePersonSchema = z.object({ id: IdSchema, name: z.string() });
export type MePerson = z.infer<typeof MePersonSchema>;
